import { useEffect, useMemo, useState, useCallback, useRef, type Dispatch, type SetStateAction } from 'react'
import { GetSurvey, GetTrackingProgress, SaveSurveyResults } from '../../../services/SurveyService'
import { SurveyLinkContext, type SurveyLinkContextValue } from './SurveyLinkContext'
import { type UploadFilesEvent, type Model, type Question, type QuestionPanelDynamicModel } from 'survey-core'
import { registerCustomQuestions } from './questions'
import { SurveyDisplay } from './SurveyDisplay'
import { SurveyNavigationButtons } from './SurveyNavigationButtons'
import { type SurveySchema, type SurveyType } from '../../../models/SurveySchema'
import { UploadFile } from '../../../services/FileService'
import { Box, Typography } from '@mui/material'
import { SurveyStepper } from './SurveyStepper'
import { handleApiResponse } from '../../../utils/apiHelpers'
import { SURVEY_COMMENT_SUFFIX, SURVEY_FILE_KEY_SUFFIX, SURVEY_FILE_SUFFIX } from '../../../constants/surveyConstants'

import 'survey-core/survey-core.min.css'
import './survey-overrides.css'

registerCustomQuestions()

const AUTO_SAVE_DELAY = 1000

interface QuestionWithMetadata extends Question {
  uploadedFileName?: string
  uploadedFileKey?: string
  panels?: Array<{
    questions: QuestionWithMetadata[]
  }>
}

function isPanelDynamicQuestion (question: Question): question is QuestionPanelDynamicModel {
  return question.getType() === 'paneldynamic' || question.getType() === 'surveypaneldynamic'
}

function hasMetadata (question: Question): question is QuestionWithMetadata {
  return 'uploadedFileName' in question || 'uploadedFileKey' in question
}

interface Props {
  annualPlanId: number
  surveyType: SurveyType
  title?: string
  navigateToSummary?: () => void
  planOrgId: number
  onSurveyLoaded?: (survey: SurveySchema) => void
  initialSurvey?: SurveySchema
  setSurvey?: Dispatch<SetStateAction<SurveySchema | undefined>>
  onExit?: () => void
  submitModalTitle?: string
  surveyVariables?: Record<string, any>
}

export function STARTSurveyDisplay ({
  annualPlanId,
  surveyType,
  title,
  navigateToSummary,
  planOrgId,
  onSurveyLoaded,
  initialSurvey,
  setSurvey,
  submitModalTitle,
  onExit,
  surveyVariables
}: Props): JSX.Element {
  const [surveyJson, setSurveyJson] = useState<any>(initialSurvey != null ? JSON.parse(initialSurvey.content) : null)
  const [loading, setLoading] = useState<boolean>(initialSurvey == null)
  const [answers, setAnswers] = useState<Record<string, any> | undefined>(initialSurvey?.answers != null ? JSON.parse(initialSurvey.answers) : undefined)
  const [surveyModel, setSurveyModel] = useState<Model | null>(null)
  const [currentPageIndex, setCurrentPageIndex] = useState(0)
  const [pageCompleteStatus, setPageCompleteStatus] = useState<boolean[]>([])
  const [visitedPages, setVisitedPages] = useState<Set<number>>(new Set([0]))
  const [priorityTaskIds, setPriorityTaskIds] = useState<Record<number, number | undefined>>({})
  const saveTimeoutRef = useRef<NodeJS.Timeout>()

  useEffect(() => {
    if (planOrgId == null) return
    void (async () => {
      const progress = await handleApiResponse(GetTrackingProgress(planOrgId))
      if (progress == null) return
      const map: Record<number, number | undefined> = {}
      progress.priorities.forEach(p => { map[p.priorityNumber] = p.planOrgTaskId })
      setPriorityTaskIds(map)
    })()
  }, [planOrgId])

  const linkContextValue: SurveyLinkContextValue = useMemo(() => ({
    rcnId: surveyVariables?.rcnId,
    planId: surveyVariables?.planId,
    planOrgId: surveyVariables?.planOrgId ?? planOrgId,
    priorityTaskIds
  }), [surveyVariables, planOrgId, priorityTaskIds])

  const extractQuestionMetadata = (question: QuestionWithMetadata, questionName: string, data: Record<string, any>): void => {
    if (question.extraInfo !== undefined) {
      data[questionName + SURVEY_COMMENT_SUFFIX] = (question.extraInfo != null && question.extraInfo !== '') ? question.extraInfo : null
    }
    if (question.uploadedFileName !== undefined) {
      data[questionName + SURVEY_FILE_SUFFIX] = (question.uploadedFileName != null && question.uploadedFileName !== '') ? question.uploadedFileName : null
    }
    if (question.uploadedFileKey !== undefined) {
      data[questionName + SURVEY_FILE_KEY_SUFFIX] = (question.uploadedFileKey != null && question.uploadedFileKey !== '') ? question.uploadedFileKey : null
    }
  }

  useEffect(() => {
    void loadSchema()
  }, [])

  const saveSurvey = useCallback(async (survey: Model, submit?: boolean): Promise<void> => {
    const data = survey.data
    survey.getAllQuestions().forEach(q => {
      const question = q as QuestionWithMetadata

      if (isPanelDynamicQuestion(q) && question.panels !== undefined) {
        question.panels.forEach((panel, index) => {
          panel.questions.forEach((panelQuestion) => {
            extractQuestionMetadata(panelQuestion, panelQuestion.name, data[question.name][index])
          })
        })
      }

      if (hasMetadata(q)) {
        extractQuestionMetadata(question, q.name, data)
      }
    })

    const answersString = JSON.stringify(data)
    await SaveSurveyResults({
      schemaId: surveyType,
      surveyAnswers: answersString,
      planOrgId,
      submit
    })

    if (setSurvey != null && submit === true) {
      setSurvey(prev => prev != null ? { ...prev, answers: answersString, isSubmitted: true } : undefined)
    }

    if (submit === true) {
      navigateToSummary?.()
    }
  }, [annualPlanId, surveyType, navigateToSummary, planOrgId, setSurvey])

  const debouncedSave = useCallback((): void => {
    if (surveyModel == null) return

    if (saveTimeoutRef.current != null) {
      clearTimeout(saveTimeoutRef.current)
    }

    saveTimeoutRef.current = setTimeout(() => {
      void saveSurvey(surveyModel)
    }, AUTO_SAVE_DELAY)
  }, [surveyModel, saveSurvey])

  const loadQuestionMetadata = useCallback((question: QuestionWithMetadata, questionName: string, savedData: Record<string, any>, survey: Model): void => {
    if (savedData[questionName + SURVEY_COMMENT_SUFFIX] != null) {
      question.extraInfo = savedData[questionName + SURVEY_COMMENT_SUFFIX]
    }
    if (savedData[questionName + SURVEY_FILE_SUFFIX] != null) {
      question.uploadedFileName = savedData[questionName + SURVEY_FILE_SUFFIX]
    }
    if (savedData[questionName + SURVEY_FILE_KEY_SUFFIX] != null) {
      question.uploadedFileKey = savedData[questionName + SURVEY_FILE_KEY_SUFFIX]

      question.onPropertyChanged.add(debouncedSave)
    }
  }, [debouncedSave])

  const updatePageCompleteStatus = useCallback((model: Model, visited: Set<number>) => {
    const statuses = model.pages.map((page, index) => {
      if (!visited.has(index)) {
        return false
      }

      const allQuestions = page.getQuestions(true)
      return allQuestions.every(q => {
        const isActuallyRequired = q.isRequired && q.isVisible
        return !isActuallyRequired || !q.isEmpty()
      })
    })
    setPageCompleteStatus(statuses)
  }, [])

  const onComplete = useCallback(async (survey: Model, _options: any): Promise<void> => {
    await saveSurvey(survey, true)
  }, [saveSurvey])

  const onLoad = useCallback(async (survey: Model): Promise<void> => {
    setSurveyModel(survey)
    survey.questionErrorLocation = 'bottom'

    const appliedTheme = {
      isPanelless: true,
      cssVariables: {
        '--sjs-general-backcolor': 'transparent',
        '--sjs-general-backcolor-dim': 'transparent',
        '--sjs-paper-backcolor': 'transparent',
        '--sjs-paper-shadow': 'none'
      }
    }

    survey.applyTheme(appliedTheme)
    survey.widthMode = 'responsive'
    survey.showTitle = false
    survey.showQuestionNumbers = 'off'
    survey.showNavigationButtons = 'none'
    survey.showCompletedPage = false
    survey.checkErrorsMode = 'onComplete'
    survey.data = answers ?? {}

    const initialVisited = new Set<number>([0])
    survey.pages.forEach((page, index) => {
      const hasAnswers = page.getQuestions(true).some(q => !q.isEmpty())
      if (hasAnswers) {
        initialVisited.add(index)
      }
    })
    setVisitedPages(initialVisited)
    updatePageCompleteStatus(survey, initialVisited)

    if (surveyVariables != null) {
      Object.entries(surveyVariables).forEach(([key, value]) => {
        survey.setVariable(key, value)
      })

      survey.getAllQuestions().forEach(q => {
        if (q.getType() === 'surveytrackingcards') {
          if (surveyVariables.planOrganizationId != null) {
            (q as any).planOrganizationId = surveyVariables.planOrganizationId
          }
          const { rcnId, planId, planOrgId } = surveyVariables
          if (rcnId != null && planId != null && planOrgId != null) {
            (q as any).priorityBasePath = `/Implementation/${String(rcnId)}/${String(planId)}/3/PlanOrganization/${String(planOrgId)}/Priority`
          }
        }
      })
    }

    survey.getAllQuestions().forEach(q => {
      const question = q as QuestionWithMetadata
      if (answers == null) return

      if (isPanelDynamicQuestion(q) && question.panels !== undefined) {
        question.panels.forEach((panel, index) => {
          if (answers[question.name]?.[index] == null) return

          panel.questions.forEach((panelQuestion) => {
            const savedData = answers[question.name][index]
            loadQuestionMetadata(panelQuestion, panelQuestion.name, savedData, survey)
          })
        })
      }

      if (hasMetadata(q)) {
        loadQuestionMetadata(question, question.name, answers, survey)
      }
    })

    survey.onUploadFiles.add((_sender: Model, { question, files, callback }: UploadFilesEvent) => {
      void Promise.all(files.map(async (file: File) => {
        try {
          const key = await UploadFile(file)
          const q = question as QuestionWithMetadata
          q.uploadedFileKey = key
          q.uploadedFileName = file.name
          return {
            file,
            content: key
          }
        } catch (e) {
          console.error('Failed to upload file', e)
          return undefined
        }
      })).then((results) => {
        const success = results.filter(r => r !== undefined)
        callback(success)
      })
    })
  }, [answers, saveSurvey, loadQuestionMetadata, updatePageCompleteStatus])

  useEffect(() => {
    if (surveyModel == null) return

    const updatePage = (): void => {
      setCurrentPageIndex(surveyModel.currentPageNo)
    }

    surveyModel.onCurrentPageChanged.add(updatePage)
    return () => {
      surveyModel.onCurrentPageChanged.remove(updatePage)
    }
  }, [surveyModel])

  useEffect(() => {
    if (surveyModel == null) return

    const checkStatus = (): void => {
      updatePageCompleteStatus(surveyModel, visitedPages)
    }

    const markPageVisited = (): void => {
      setVisitedPages(prev => {
        if (prev.has(surveyModel.currentPageNo)) return prev
        return new Set(prev).add(surveyModel.currentPageNo)
      })
    }

    checkStatus()
    markPageVisited()

    surveyModel.onValueChanged.add(checkStatus)
    surveyModel.onValueChanged.add(debouncedSave)
    surveyModel.onCurrentPageChanged.add(checkStatus)
    surveyModel.onCurrentPageChanged.add(markPageVisited)
    return () => {
      surveyModel.onValueChanged.remove(checkStatus)
      surveyModel.onValueChanged.remove(debouncedSave)
      surveyModel.onCurrentPageChanged.remove(checkStatus)
      surveyModel.onCurrentPageChanged.remove(markPageVisited)
      if (saveTimeoutRef.current != null) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [surveyModel, visitedPages, updatePageCompleteStatus, debouncedSave])

  const loadSchema = async (): Promise<void> => {
    if (initialSurvey != null) {
      onSurveyLoaded?.(initialSurvey)
      setLoading(false)
      return
    }

    const survey = await handleApiResponse(GetSurvey(surveyType, planOrgId))
    if (survey != null) {
      const schemaJson = JSON.parse(survey.content)
      setSurveyJson(schemaJson)
      if (survey.answers != null) setAnswers(JSON.parse(survey.answers))
      onSurveyLoaded?.(survey)
    }
    setLoading(false)
  }

  if (loading) return <div>Loading...</div>

  const showStepper = surveyJson?.pages?.length > 1

  return (
    <SurveyLinkContext.Provider value={linkContextValue}>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ mt: 2, ml: 5 }}>
          <Typography variant="h3">{title}</Typography>
        </Box>
        {showStepper && surveyModel != null && (
          <SurveyStepper
            surveyModel={surveyModel}
            currentPageIndex={currentPageIndex}
            pageCompleteStatus={pageCompleteStatus}
          />
        )}
        {surveyJson !== null
          ? (
            <SurveyDisplay
              surveyJson={surveyJson}
              onComplete={onComplete}
              onLoad={onLoad}
            />
            )
          : <div>No schema found</div>
          }
        {surveyModel != null && (
          <SurveyNavigationButtons
            survey={surveyModel}
            saveSurvey={saveSurvey}
            submitModalTitle={submitModalTitle}
            onExit={onExit}
          />
        )}
      </Box>
    </SurveyLinkContext.Provider>
  )
}
